import { Helmet } from 'react-helmet-async';

const defaultMeta = {
  siteName: 'GuidePro Inc.',
  siteUrl: 'https://guideproinc.com',
  defaultTitle: 'GuidePro Inc. | AI-Powered Guidewire Implementation Partner',
  defaultDescription: 'GuidePro Inc. delivers AI-powered Guidewire implementation, cloud migration, QA testing, and staffing solutions for modern insurers. Transform your insurance technology with intelligent automation.',
  defaultKeywords: 'Guidewire, Guidewire implementation, AI insurance solutions, PolicyCenter, ClaimCenter, BillingCenter, Guidewire Cloud, insurance digital transformation, Guidewire consulting, insurance technology, AI-powered insurance, intelligent automation, Guidewire partner, insurance modernization, cloud migration insurance',
  defaultImage: '/guidepro-logo.png',
};

export const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noIndex = false,
}) => {
  const seoTitle = title 
    ? `${title} | ${defaultMeta.siteName}` 
    : defaultMeta.defaultTitle;
  
  const seoDescription = description || defaultMeta.defaultDescription;
  const seoKeywords = keywords 
    ? `${keywords}, ${defaultMeta.defaultKeywords}` 
    : defaultMeta.defaultKeywords;
  const seoImage = image || defaultMeta.defaultImage;
  const seoUrl = url || defaultMeta.siteUrl;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content="GuidePro Inc." />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:site_name" content={defaultMeta.siteName} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      
      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#0ea5e9" />
      <link rel="canonical" href={seoUrl} />
    </Helmet>
  );
};

// Page-specific SEO configurations
export const pageSEO = {
  home: {
    title: null, // Uses default
    description: 'GuidePro Inc. is your AI-powered Guidewire implementation partner. We deliver intelligent automation, cloud migration, QA testing, and staffing solutions for modern insurers.',
    keywords: 'AI-powered Guidewire, insurance digital transformation, Guidewire implementation partner, intelligent insurance automation',
  },
  about: {
    title: 'About Us',
    description: 'Learn about GuidePro Inc. - a leading AI-powered Guidewire implementation partner with 500+ certified experts delivering transformative insurance technology solutions.',
    keywords: 'GuidePro team, Guidewire experts, insurance technology company, Guidewire certified professionals',
  },
  services: {
    title: 'Services',
    description: 'Comprehensive AI-powered Guidewire services including PolicyCenter, ClaimCenter, BillingCenter implementation, cloud migration, QA testing, and expert staffing.',
    keywords: 'Guidewire services, PolicyCenter implementation, ClaimCenter services, BillingCenter consulting, Guidewire cloud services',
  },
  policyCenter: {
    title: 'PolicyCenter Implementation',
    description: 'Expert Guidewire PolicyCenter implementation and optimization services. Transform your policy administration with AI-driven automation and intelligent workflows.',
    keywords: 'PolicyCenter implementation, policy administration system, Guidewire PolicyCenter, policy lifecycle management, AI policy automation',
  },
  claimCenter: {
    title: 'ClaimCenter Implementation',
    description: 'Streamline claims processing with our Guidewire ClaimCenter implementation services. AI-powered claims automation for faster, accurate settlements.',
    keywords: 'ClaimCenter implementation, claims management system, Guidewire ClaimCenter, AI claims processing, claims automation',
  },
  billingCenter: {
    title: 'BillingCenter Implementation',
    description: 'Modernize your insurance billing with Guidewire BillingCenter implementation. Intelligent billing automation and payment processing solutions.',
    keywords: 'BillingCenter implementation, insurance billing system, Guidewire BillingCenter, billing automation, payment processing',
  },
  cloudModernisation: {
    title: 'Cloud Migration & Modernisation',
    description: 'Accelerate your Guidewire Cloud migration with our expert services. AI-driven modernization strategies for seamless cloud adoption.',
    keywords: 'Guidewire Cloud, cloud migration insurance, Guidewire modernization, cloud adoption, insurance cloud platform',
  },
  qaTesting: {
    title: 'QA & Testing Services',
    description: 'Comprehensive Guidewire QA and testing services. AI-enhanced test automation, performance testing, and quality assurance for insurance platforms.',
    keywords: 'Guidewire testing, QA services insurance, test automation, performance testing, Guidewire quality assurance',
  },
  staffing: {
    title: 'Staffing & Professional Talent',
    description: 'Access top Guidewire talent with our staffing solutions. Certified professionals for implementation, support, and project augmentation.',
    keywords: 'Guidewire staffing, insurance IT talent, Guidewire professionals, contract staffing, Guidewire consultants',
  },
  caseStudies: {
    title: 'Case Studies',
    description: 'Explore our Guidewire implementation success stories. Real-world AI-powered transformations delivering measurable results for leading insurers.',
    keywords: 'Guidewire case studies, insurance success stories, implementation results, digital transformation examples',
  },
  resources: {
    title: 'Resources',
    description: 'Access Guidewire whitepapers, blogs, and webinars. Expert insights on AI-powered insurance technology and digital transformation.',
    keywords: 'Guidewire resources, insurance whitepapers, Guidewire blog, insurance webinars, thought leadership',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with GuidePro Inc. for your Guidewire implementation needs. Request a consultation for AI-powered insurance solutions.',
    keywords: 'contact GuidePro, Guidewire consultation, insurance technology contact, request demo',
  },
};
